import React from 'react';
import { Link } from 'react-router-dom';
import './PricingSection.css';

const plans = [
  {
    name: 'Starter AI',
    setupFee: '$297',
    monthlyFee: '$79',
    features: [
      '1 custom automation — e.g. automatic welcome email when a lead fills out the contact form, or automatic appointment reminder by email',
      'Basic chatbot on the website — answers the most common questions (hours, address, pricing) and captures visitor name/email',
      '7 business days to go live',
      '1 round of revisions included after delivery',
      '30 days of email support after launch',
    ],
    buttonClass: 'btn-three',
  },
  {
    name: 'Business AI',
    setupFee: '$597',
    monthlyFee: '$147',
    mostPopular: true,
    features: [
      '3 custom automations — e.g. welcome email + follow-up on unanswered leads + automatic update of a client tracking sheet (Google Sheets / light CRM)',
      'Advanced chatbot — answers FAQs, captures qualified leads (name/email/phone), and forwards complex requests to the owner by email',
      'Basic analytics dashboard — conversations, leads captured, response rate, live link, auto-updated',
      '10 business days to go live',
      '2 rounds of revisions included after delivery',
      '60 days of email support after launch',
    ],
    buttonClass: 'btn-one',
  },
  {
    name: 'Growth AI',
    setupFee: '$997',
    monthlyFee: '$297',
    features: [
      '5 custom automations — e.g. welcome email + lead follow-up + CRM update + internal notification for hot leads + automatic weekly report by email',
      'Full AI chatbot — answers questions, qualifies leads (budget/need/urgency), books appointments directly into the calendar, forwards complex requests with a summary',
      'Advanced analytics dashboard — conversations, leads, conversion rate, week-over-week comparison, automatic Monday report',
      'Full CRM integration — contacts and automations centralized in one place',
      '15 business days to go live',
      'Unlimited revisions during the delivery period',
      '90 days of priority support + 30-min onboarding call to set everything up together',
    ],
    buttonClass: 'btn-three',
  },
];

const PricingSection = () => {
  return (
    <>
      <div className='container'>
        <section className="pricing-style-two home-5 pb_120 centred p_relative container" id='pricing-sec'>
          <div className="pattern-layer ">
            <div
              className="pattern-1"
              style={{ backgroundImage: "url(assets/images/shape/shape-42.png)" }}
            />
            <div
              className="pattern-2"
              style={{ backgroundImage: "url(assets/images/shape/shape-43.png)" }}
            />
          </div>
          <div className="auto-container">
            <div className="sec-title mb_55">
              <span data-animation-text="" className="sub-title ">
                Pricing Plans
              </span>
              <h2>One-Time Setup + Monthly AI Plans</h2>
              <p className="pricing-section-intro">
                Clear pricing for setup and ongoing support. All plans are monthly subscriptions and can be cancelled anytime.
              </p>
            </div>

            <div className="row clearfix">
              {plans.map((plan, index) => (
                <div className="col-lg-4 col-md-6 col-sm-12 pricing-block" key={plan.name}>
                  <div
                    className={`pricing-block-two wow fadeInUp animated ${plan.mostPopular ? 'active-block' : ''}`}
                    data-wow-delay={`${index * 200}ms`}
                    data-wow-duration="1500ms"
                  >
                    <div className="pricing-table">
                      <div className="table-header mb_40">
                        {plan.mostPopular ? <span className="pricing-popular-badge">Most Popular</span> : null}
                        <h3>{plan.name}</h3>
                        <p className="pricing-period">One-time setup fee + monthly subscription</p>
                        <div className="pricing-charge-summary">
                          <div className="pricing-charge-line">
                            <span className="pricing-charge-label">Setup</span>
                            <strong className="pricing-charge-value">{plan.setupFee}</strong>
                          </div>
                          <div className="pricing-charge-line pricing-charge-line-monthly">
                            <span className="pricing-charge-label">Monthly</span>
                            <strong className="pricing-charge-value">
                              {plan.monthlyFee}
                              <span>/month</span>
                            </strong>
                          </div>
                        </div>
                      </div>
                      <div className="table-content">
                        <ul className="feature-list clearfix">
                          {plan.features.map((feature) => (
                            <li key={feature}>{feature}</li>
                          ))}
                        </ul>
                        <Link to="/contact-us" className={`theme-btn ${plan.buttonClass}`}>
                          Get Started
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Standalone AI Voice Receptionist Section */}
            <div className="row justify-content-center mt_50 text-start">
              <div className="col-lg-10 col-md-12">
                <div 
                  className="pricing-voice-receptionist wow fadeInUp animated"
                  data-wow-delay="300ms"
                  data-wow-duration="1500ms"
                >
                  <div className="voice-card-header">
                    <div className="voice-header-left">
                      <span className="voice-badge">Standalone Offer</span>
                      <h3>AI Voice Receptionist</h3>
                      <p className="voice-tagline">“Your business never misses a call again”</p>
                    </div>
                    <div className="voice-pricing">
                      <div className="voice-pricing-line">
                        <span className="voice-price-label">Setup</span>
                        <strong className="voice-price-value">$600</strong>
                      </div>
                      <div className="voice-pricing-line voice-pricing-monthly">
                        <span className="voice-price-label">Monthly</span>
                        <strong className="voice-price-value">
                          $397
                          <span>/month</span>
                        </strong>
                      </div>
                    </div>
                  </div>
                  <div className="voice-card-body">
                    <ul className="voice-feature-list">
                      <li>
                        <strong>24/7 automatic call answering</strong> — a dedicated number answers every incoming call, even at night, weekends, or during service
                      </li>
                      <li>
                        <strong>Live reservation booking</strong> — the AI collects name, phone, party size, date and time, then automatically creates the event in Google Calendar — no human needed
                      </li>
                      <li>
                        <strong>FAQ handling</strong> — hours, address, menu/pricing, allergens, parking, etc., from a knowledge base specific to the business
                      </li>
                      <li>
                        <strong>Missed-call SMS fallback</strong> (plan-dependent)
                      </li>
                      <li>
                        <strong>Call summary</strong> automatically logged to a tracking sheet (Google Sheets), viewable anytime
                      </li>
                      <li>
                        <strong>Guaranteed 7-day setup</strong>
                      </li>
                    </ul>
                    <div className="voice-actions">
                      <Link to="/contact-us" className="theme-btn btn-one voice-btn">
                        Get Started
                      </Link>
                      <a 
                        href="https://le-petit-bistrot.web.app/" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="theme-btn btn-three voice-btn voice-demo-btn"
                      >
                        <i className="fas fa-play-circle mr-2"></i> Try Live Demo
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </section>
      </div>
    </>
  );
};

export default PricingSection;

